// Gestão DNA · Layout premium (rota /gestao)
// Sidebar escura institucional + conteúdo claro, busca global de aluno, breadcrumbs.
// Renderiza SOMENTE módulos com permissão. Nenhum item financeiro existe aqui.
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Home, Search, LogOut, Loader2, ShieldCheck, ChevronRight, Menu, X, Lock,
} from 'lucide-react';
import api from '@/lib/api';
import { usePermV2 } from '@/lib/perm_v2';
import { clearAuth, getUser } from '@/lib/auth';
import { GESTAO_MODULES, GESTAO_GRUPOS, moduleAllowed } from './registry';
import { PortalGestaoProvider, usePortalGestao } from './usePortalGestao';

function GlobalStudentSearch() {
  const [q, setQ] = useState('');
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const boxRef = useRef(null);

  useEffect(() => {
    const term = q.trim();
    if (term.length < 2) { setItems([]); return; }
    const t = setTimeout(async () => {
      setLoading(true);
      try {
        const { data } = await api.get('/v2/aluno/search', { params: { q: term, limit: 8 } });
        setItems(Array.isArray(data) ? data : (data?.items || []));
        setOpen(true);
      } catch (_) { setItems([]); } finally { setLoading(false); }
    }, 260);
    return () => clearTimeout(t);
  }, [q]);

  useEffect(() => {
    const onClick = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, []);

  return (
    <div className="relative w-full max-w-md" ref={boxRef}>
      <div className="flex items-center gap-2 rounded-full border border-slate-200 bg-white px-4 py-2 shadow-sm
                      focus-within:border-violet-300 focus-within:ring-4 focus-within:ring-violet-100 transition-all">
        {loading ? <Loader2 className="w-4 h-4 animate-spin text-violet-500" />
          : <Search className="w-4 h-4 text-slate-400" />}
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          onFocus={() => items.length && setOpen(true)}
          placeholder="Buscar aluno, matrícula, turma ou responsável..."
          className="w-full bg-transparent text-sm text-slate-700 placeholder:text-slate-400 outline-none"
          data-testid="gestao-global-search"
        />
      </div>
      <AnimatePresence>
        {open && items.length > 0 && (
          <motion.ul
            initial={{ opacity: 0, y: -6 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -6 }}
            transition={{ duration: 0.16 }}
            className="absolute z-50 mt-2 w-full overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-xl"
            data-testid="gestao-search-results"
          >
            {items.map((s) => (
              <li key={s.id}>
                <button
                  type="button"
                  onClick={() => { setOpen(false); setQ(''); navigate(`/gestao/alunos/${s.id}`); }}
                  className="flex w-full items-center gap-3 px-4 py-3 text-left hover:bg-violet-50/70 transition-colors"
                  data-testid={`gestao-search-item-${s.id}`}
                >
                  <span className="flex h-8 w-8 items-center justify-center rounded-full bg-violet-100 text-xs font-bold text-violet-700">
                    {(s.name || '?').slice(0, 2).toUpperCase()}
                  </span>
                  <span className="min-w-0">
                    <span className="block truncate text-sm font-semibold text-slate-800">{s.name}</span>
                    <span className="block truncate text-xs text-slate-500">
                      {s.registration_number || 'sem matrícula'}
                      {s.class_name ? ` · ${s.class_name}` : ''}
                      {s.responsible_name ? ` · resp.: ${s.responsible_name}` : ''}
                    </span>
                  </span>
                </button>
              </li>
            ))}
          </motion.ul>
        )}
      </AnimatePresence>
    </div>
  );
}

function Breadcrumbs() {
  const { pathname } = useLocation();
  const parts = pathname.replace(/^\/gestao\/?/, '').split('/').filter(Boolean);
  const label = (p) => {
    const m = GESTAO_MODULES.find((x) => x.route.endsWith(`/${p}`));
    return m ? m.label : decodeURIComponent(p).slice(0, 28);
  };
  return (
    <nav className="flex items-center gap-1.5 text-xs text-slate-500" data-testid="gestao-breadcrumbs">
      <NavLink to="/gestao" className="font-semibold text-slate-600 hover:text-violet-700">Gestão DNA</NavLink>
      {parts.map((p, i) => (
        <span key={`${p}-${i}`} className="flex items-center gap-1.5">
          <ChevronRight className="w-3 h-3 text-slate-300" />
          <span className={i === parts.length - 1 ? 'font-semibold text-violet-700' : ''}>{label(p)}</span>
        </span>
      ))}
    </nav>
  );
}

function GestaoInner() {
  const { can, perms, loading } = usePermV2();
  const { logAudit, dnagoAutoGrant } = usePortalGestao();
  const navigate = useNavigate();
  const location = useLocation();
  const user = getUser();
  const [mobileOpen, setMobileOpen] = useState(false);

  const isAdmin = perms?.is_admin_bypass === true;
  const allowed = useMemo(
    () => GESTAO_MODULES.filter((m) => moduleAllowed(m, { can, isAdmin, dnagoAutoGrant })),
    [can, isAdmin, dnagoAutoGrant],
  );

  useEffect(() => {
    if (!perms) return;
    logAudit({ action: 'navigate', page: location.pathname, target_type: 'page' });
    setMobileOpen(false);
  }, [location.pathname, perms, logAudit]);

  const logout = useCallback(() => { clearAuth(); navigate('/login'); }, [navigate]);

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#0D0614]" data-testid="gestao-loading">
        <Loader2 className="h-6 w-6 animate-spin text-violet-400" />
      </div>
    );
  }

  const canPortal = isAdmin || can('portal_gestao', 'visualizar');
  if (!canPortal) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#0D0614] p-6" data-testid="gestao-denied">
        <div className="max-w-md rounded-3xl border border-white/10 bg-white/5 p-10 text-center backdrop-blur-xl">
          <Lock className="mx-auto mb-4 h-10 w-10 text-violet-300" />
          <h2 className="text-lg font-bold text-white">Sem acesso ao Gestão DNA</h2>
          <p className="mt-2 text-sm text-slate-300">
            Solicite ao administrador a permissão <code className="rounded bg-white/10 px-1">portal_gestao.visualizar</code>.
          </p>
          <button onClick={() => navigate('/login')}
            className="mt-6 rounded-full bg-violet-600 px-5 py-2.5 text-sm font-semibold text-white hover:bg-violet-500 transition-colors"
            data-testid="gestao-back-login">Voltar ao login</button>
        </div>
      </div>
    );
  }

  const grupos = GESTAO_GRUPOS.filter((g) => allowed.some((m) => m.grupo === g));
  const first = (user?.name || user?.email || 'Equipe').split(' ')[0];

  const SideContent = (
    <>
      <div className="flex items-center gap-3 px-6 py-6">
        <div className="flex h-10 w-10 items-center justify-center rounded-2xl bg-gradient-to-br from-violet-500 to-fuchsia-600 shadow-lg shadow-violet-900/40">
          <ShieldCheck className="h-5 w-5 text-white" />
        </div>
        <div className="leading-tight">
          <div className="text-sm font-extrabold tracking-tight text-white">Gestão DNA</div>
          <div className="text-[10px] font-bold uppercase tracking-[0.22em] text-violet-300/80">Novo DNA</div>
        </div>
      </div>

      <nav className="flex-1 overflow-y-auto px-3 pb-6">
        <NavLink to="/gestao" end className={({ isActive }) =>
          `group relative mb-1 flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-semibold transition-all duration-200
           ${isActive ? 'bg-white/10 text-white' : 'text-slate-300 hover:bg-white/5 hover:text-white'}`}
          data-testid="gestao-nav-home">
          {({ isActive }) => (
            <>
              {isActive && <span className="absolute left-0 top-1/2 h-5 w-[3px] -translate-y-1/2 rounded-r-full bg-violet-400 shadow-[0_0_10px_rgba(139,92,246,.8)]" />}
              <Home className="h-[18px] w-[18px]" /> Início
            </>
          )}
        </NavLink>

        {grupos.map((g) => (
          <div key={g} className="mt-5">
            <div className="mb-2 px-3 text-[10px] font-bold uppercase tracking-[0.2em] text-slate-500">{g}</div>
            {allowed.filter((m) => m.grupo === g).map((m) => (
              <NavLink key={m.key} to={m.route} className={({ isActive }) =>
                `group relative mb-1 flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium transition-all duration-200
                 ${isActive ? 'bg-white/10 text-white' : 'text-slate-300 hover:bg-white/5 hover:text-white'}`}
                data-testid={`gestao-nav-${m.key}`}>
                {({ isActive }) => (
                  <>
                    {isActive && <span className="absolute left-0 top-1/2 h-5 w-[3px] -translate-y-1/2 rounded-r-full bg-violet-400 shadow-[0_0_10px_rgba(139,92,246,.8)]" />}
                    <m.icon className="h-[18px] w-[18px]" />
                    <span className="flex-1 truncate">{m.label}</span>
                    {m.readOnly && <span className="rounded-full bg-white/10 px-1.5 py-0.5 text-[9px] font-bold uppercase text-slate-300">leitura</span>}
                  </>
                )}
              </NavLink>
            ))}
          </div>
        ))}
      </nav>

      <div className="border-t border-white/5 px-5 py-4">
        <div className="mb-3 flex items-center gap-3">
          <div className="flex h-9 w-9 items-center justify-center rounded-full bg-white/10 text-xs font-bold text-white">
            {first.slice(0, 2).toUpperCase()}
          </div>
          <div className="min-w-0">
            <div className="truncate text-xs font-bold text-white">{user?.name || user?.email}</div>
            <div className="truncate text-[10px] uppercase tracking-wider text-slate-400">{user?.role || 'equipe'}</div>
          </div>
        </div>
        <button onClick={logout}
          className="flex w-full items-center justify-center gap-2 rounded-xl border border-white/10 py-2 text-xs font-semibold text-slate-300 transition-colors hover:bg-white/5 hover:text-white"
          data-testid="gestao-logout">
          <LogOut className="h-3.5 w-3.5" /> Sair
        </button>
      </div>
    </>
  );

  return (
    <div className="flex min-h-screen bg-[#F7F5FB]" data-testid="gestao-layout">
      <aside className="sticky top-0 hidden h-screen w-[264px] flex-col bg-gradient-to-b from-[#2A1240] to-[#24103A] lg:flex"
        data-testid="gestao-sidebar">
        {SideContent}
      </aside>

      <AnimatePresence>
        {mobileOpen && (
          <motion.div className="fixed inset-0 z-40 lg:hidden" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
            <div className="absolute inset-0 bg-black/50" onClick={() => setMobileOpen(false)} />
            <motion.aside
              initial={{ x: -280 }} animate={{ x: 0 }} exit={{ x: -280 }} transition={{ type: 'spring', damping: 26, stiffness: 240 }}
              className="relative flex h-full w-[264px] flex-col bg-gradient-to-b from-[#2A1240] to-[#24103A]"
              data-testid="gestao-sidebar-mobile">
              {SideContent}
            </motion.aside>
          </motion.div>
        )}
      </AnimatePresence>

      <div className="flex min-w-0 flex-1 flex-col">
        <header className="sticky top-0 z-30 border-b border-slate-200/70 bg-white/80 backdrop-blur-xl">
          <div className="flex flex-col gap-3 px-5 py-3 md:flex-row md:items-center md:justify-between md:px-8 md:py-4">
            <div className="flex items-center gap-3">
              <button onClick={() => setMobileOpen((v) => !v)}
                className="rounded-xl border border-slate-200 p-2 text-slate-600 lg:hidden"
                data-testid="gestao-menu-toggle">
                {mobileOpen ? <X className="h-4 w-4" /> : <Menu className="h-4 w-4" />}
              </button>
              <Breadcrumbs />
            </div>
            <GlobalStudentSearch />
          </div>
        </header>

        <motion.main key={location.pathname}
          initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.22, ease: 'easeOut' }}
          className="min-w-0 flex-1 px-5 py-6 md:px-8 md:py-10" data-testid="gestao-content">
          <Outlet context={{ allowed, isAdmin }} />
        </motion.main>
      </div>
    </div>
  );
}

export default function GestaoLayout() {
  return (
    <PortalGestaoProvider>
      <GestaoInner />
    </PortalGestaoProvider>
  );
}
